/**
 * Property Manager application page (Phase 2L-A111 / Phase 1).
 *
 * Public route at /property-managers/apply. Submits to
 * POST /api/property-manager-applications. Read-only after submit —
 * the applicant then waits for the admin approval email.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, CheckCircle2, ArrowLeft, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { pushFormEvent } from "@/lib/analytics";
import { BrandLogo } from "@/components/BrandLogo";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const SUBURB_HINT = "Comma-separated: e.g. Noosa Heads, Cooroy, Doonan";

const initial = {
  business_name: "", contact_name: "", email: "", phone: "",
  office_address: "", areas_managed: "",
  approx_rural_property_count: "", notes: "",
  consent_acknowledged: false,
};

export default function PropertyManagerApply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // SCWT-PM-V2 Phase 8 · Feb 2026 — capture PWS-Partner attribution
  // from the marketing link (?p={partner_slug}) and show the referring
  // Partner's name so the applicant knows how they arrived.
  const [searchParams] = useSearchParams();
  const partnerSlug = (searchParams.get("p") || "").trim().toLowerCase();
  const [referringPartner, setReferringPartner] = useState(null);

  useEffect(() => {
    if (!partnerSlug) return;
    let cancelled = false;
    api.get(`/partners/resolve-slug/${encodeURIComponent(partnerSlug)}`)
      .then(({ data }) => { if (!cancelled) setReferringPartner(data); })
      .catch(() => { /* silently ignore: unknown slug never blocks the form */ });
    return () => { cancelled = true; };
  }, [partnerSlug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent_acknowledged) {
      setError("Please acknowledge that Aware will review your application before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        areas_managed: form.areas_managed
          .split(",").map((s) => s.trim()).filter(Boolean),
        approx_rural_property_count: form.approx_rural_property_count === ""
          ? null
          : Number(form.approx_rural_property_count),
        referred_by_partner_slug: partnerSlug || null,
      };
      const { data } = await api.post("/property-manager-applications", payload);
      pushFormEvent("registration_form_submit", {
        form_name: "property_manager_application",
        form_type: "registration",
        registration_type: "property_manager",
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit application — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
            <BrandLogo to="/" size="sm" />
            <Link to="/" className="text-xs text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to home
            </Link>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div data-testid="pm-apply-success" className="bg-white rounded-3xl border border-emerald-200 p-10 text-center shadow-sm">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-display text-3xl font-bold text-slate-900">
              {result.duplicate ? "We already have your application" : "Application received"}
            </h1>
            <p className="mt-3 text-slate-600 max-w-lg mx-auto">
              {result.duplicate
                ? "An application for this email is already in our queue. Our team will be in touch shortly — there's no need to resubmit."
                : "Thanks — we've received your Property Manager application. A confirmation email is on its way. Our team will review your application and respond within 1-2 business days."}
            </p>
            <Link
              to="/"
              data-testid="pm-apply-success-home"
              className="mt-7 inline-flex items-center rounded-full bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 text-sm font-semibold transition"
            >
              Back to home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <Link to="/" className="text-xs text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
          <Building2 className="w-3.5 h-3.5" /> Property Manager Portal
        </div>
        <h1 data-testid="pm-apply-h1" className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">
          Apply to manage rural water testing on behalf of your portfolio
        </h1>
        {referringPartner?.business_name && (
          <div
            data-testid="pm-apply-referring-partner"
            className="mt-4 rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-900"
          >
            Introduced by <strong>{referringPartner.business_name}</strong>
            {referringPartner.franchise_region ? (
              <span> — your local Pristine Water Systems partner in {referringPartner.franchise_region}.</span>
            ) : (
              <span> — your local Pristine Water Systems partner.</span>
            )}
          </div>
        )}
        <p className="mt-3 text-slate-600 max-w-2xl">
          Tell us about your agency and the rural / tank-water properties
          you manage. Once approved you'll get access to a Water Health
          AWARE Water Health Record dashboard showing the latest test result for every
          managed property — perfect for landlord reporting and tenant
          peace of mind.
        </p>

        <form data-testid="pm-apply-form" onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-slate-200 p-7 space-y-5 shadow-sm">
          {error && (
            <div data-testid="pm-apply-error" className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">
              {error}
            </div>
          )}
          <Row label="Agency / business name *" testId="pm-apply-business-name">
            <input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} />
          </Row>
          <div className="grid sm:grid-cols-2 gap-5">
            <Row label="Contact person *" testId="pm-apply-contact-name">
              <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} />
            </Row>
            <Row label="Phone *" testId="pm-apply-phone">
              <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
            </Row>
          </div>
          <Row label="Email *" testId="pm-apply-email" hint="Used as your login once approved.">
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </Row>
          <Row label="Office address" testId="pm-apply-office-address" hint="Start typing — we'll suggest matching Australian addresses.">
            <AddressAutocomplete
              value={form.office_address}
              onChange={(v) => set("office_address", v)}
              placeholder="123 Hastings Street, Noosa Heads QLD"
              testId="pm-apply-office-address-input"
            />
          </Row>
          <Row label="Suburbs / areas managed" hint={SUBURB_HINT} testId="pm-apply-areas-managed">
            <input value={form.areas_managed} onChange={(e) => set("areas_managed", e.target.value)} className={inputCls} />
          </Row>
          <Row label="Approx. number of rural / tank-water properties you manage" testId="pm-apply-property-count">
            <input type="number" min="0" max="10000" value={form.approx_rural_property_count}
                   onChange={(e) => set("approx_rural_property_count", e.target.value)} className={inputCls} />
          </Row>
          <Row label="Notes / message" testId="pm-apply-notes">
            <textarea rows="4" value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
          </Row>
          <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-cyan-50/60 border border-cyan-100 px-4 py-3">
            <input
              type="checkbox"
              checked={form.consent_acknowledged}
              data-testid="pm-apply-consent"
              onChange={(e) => set("consent_acknowledged", e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-slate-700">
              I acknowledge Aware will review this application and may
              contact me for further information before approving access
              to the Property Manager Portal.
            </span>
          </label>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500 inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              We never share your data. Approval typically takes 1-2 business days.
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="pm-apply-submit"
              className="inline-flex items-center rounded-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition"
            >
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200 bg-white";

function Row({ label, hint, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `${testId}-row` : undefined}>
      <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{label}</span>
      {hint && <span className="block text-[11px] text-slate-400 mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
